import { type KeyboardEvent, useEffect, useMemo, useState } from "react";
import {
  BeakerIcon,
  ClockIcon,
  FileTextIcon,
  GaugeIcon,
  SquarePenIcon,
  UserIcon,
} from "lucide-react";

import { Column } from "@/components/Column";
import { Row } from "@/components/Row";
import { TimeAgoInTableCell } from "@/components/TimeAgoInTableCell";
import { JobBottleneckBadge } from "@/components/job-tracker/badges/JobBottleneckBadge";
import { TableJobDifficultyBadge } from "@/components/job-tracker/badges/TableJobDifficultyBadge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { useTaggableUsers } from "@/contexts/TaggableUsersContext";
import { useCreateJobUpdate } from "@/hooks/mutations/useCreateJobUpdate";
import { useGetJobUpdates } from "@/hooks/queries/useGetJobUpdates";
import { cn } from "@/lib/utils";
import { type JobUpdate } from "@/types";

// Minimal job shape the cell needs — satisfied by both the portfolio matrix
// job and the dashboard job.
export interface PulseCellJob {
  id: string;
  companyId: string;
  companyName: string;
  title: string;
  pulse?: string | null;
  pulseDescription?: string | null;
  // When provided, the cell shows a small time-ago under the note.
  pulseCreatedAt?: string | null;
}

const PULSE_TEXT_CLASS =
  "line-clamp-3 overflow-hidden whitespace-normal text-ellipsis text-sm leading-snug text-slate-700";

const getPulseDescription = (job: PulseCellJob): string =>
  job.pulseDescription ?? job.pulse ?? "";

const getDescriptionHistory = (jobUpdates: JobUpdate[]): JobUpdate[] =>
  [
    ...new Map(
      jobUpdates.map((jobUpdate) => [jobUpdate.id, jobUpdate]),
    ).values(),
  ]
    .filter((jobUpdate) => {
      const description = jobUpdate.description?.trim();
      return Boolean(description);
    })
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );

const PulseHistoryTable = ({
  history,
  isError,
  isLoading,
}: {
  history: JobUpdate[];
  isError: boolean;
  isLoading: boolean;
}) => {
  const { userNameMap } = useTaggableUsers();

  if (isLoading) {
    return (
      <p className="rounded-lg border border-dashed border-slate-200 px-4 py-6 text-center text-sm text-slate-500">
        Loading pulse history...
      </p>
    );
  }

  if (isError) {
    return (
      <p className="rounded-lg border border-dashed border-slate-200 px-4 py-6 text-center text-sm text-slate-500">
        Could not load pulse history.
      </p>
    );
  }

  if (history.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-slate-200 px-4 py-6 text-center text-sm text-slate-500">
        No previous pulse notes yet.
      </p>
    );
  }

  return (
    <div className="max-h-[280px] overflow-auto rounded-lg border border-slate-200">
      <table className="w-full table-fixed text-sm">
        <TableHeader className="sticky top-0 bg-slate-50 [&_tr]:border-b-0">
          <TableRow className="sticky-border-bottom">
            <TableHead className="w-[34%]">
              <Row className="items-center gap-2 text-slate-700">
                <FileTextIcon className="h-4 w-4" />
                Note
              </Row>
            </TableHead>
            <TableHead className="w-[14%]">
              <Row className="items-center gap-2 text-slate-700">
                <UserIcon className="h-4 w-4" />
                By
              </Row>
            </TableHead>
            <TableHead className="w-[16%]">
              <Row className="items-center gap-2 text-slate-700">
                <GaugeIcon className="h-4 w-4" />
                Difficulty
              </Row>
            </TableHead>
            <TableHead className="w-[18%]">
              <Row className="items-center gap-2 text-slate-700">
                <BeakerIcon className="h-4 w-4" />
                Bottleneck
              </Row>
            </TableHead>
            <TableHead className="w-[18%]">
              <Row className="items-center gap-2 text-slate-700">
                <ClockIcon className="h-4 w-4" />
                Created
              </Row>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {history.map((entry) => (
            <TableRow key={entry.id}>
              <TableCell className="px-2 py-3 align-top">
                <p className="whitespace-pre-wrap text-sm leading-snug text-slate-700">
                  {entry.description}
                </p>
              </TableCell>
              <TableCell className="px-2 py-3 align-top">
                <p className="truncate text-sm text-slate-700">
                  {userNameMap[entry.userId] ?? "Unknown"}
                </p>
              </TableCell>
              <TableCell className="px-2 py-3 align-top">
                {entry.difficulty != null ? (
                  <TableJobDifficultyBadge
                    className="max-w-full"
                    difficulty={entry.difficulty}
                  />
                ) : null}
              </TableCell>
              <TableCell className="px-2 py-3 align-top">
                {entry.bottleneck != null ? (
                  <JobBottleneckBadge bottleneck={entry.bottleneck} />
                ) : null}
              </TableCell>
              <TableCell className="px-2 py-3 align-top">
                <TimeAgoInTableCell timestamp={entry.createdAt} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </table>
    </div>
  );
};

const PulseEditDialog = ({
  open,
  onOpenChange,
  companyName,
  jobTitle,
  pulse,
  history,
  isHistoryError,
  isHistoryLoading,
  onSave,
  isSaving,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyName: string;
  jobTitle: string;
  pulse: string;
  history: JobUpdate[];
  isHistoryError: boolean;
  isHistoryLoading: boolean;
  onSave: (nextPulse: string) => void;
  isSaving: boolean;
}) => {
  const [draft, setDraft] = useState("");

  useEffect(() => {
    if (open) {
      setDraft("");
    }
  }, [open]);

  const sortedHistory = useMemo(
    () =>
      [...history].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      ),
    [history],
  );

  const trimmedDraft = draft.trim();
  const canSave =
    !isSaving && trimmedDraft.length > 0 && trimmedDraft !== pulse;

  const handleSave = () => {
    if (!canSave) return;
    onSave(trimmedDraft);
    onOpenChange(false);
  };

  // Cmd/Ctrl + Enter saves without leaving the textarea.
  const handleDraftKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
      event.preventDefault();
      handleSave();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex max-h-[85vh] max-w-3xl flex-col gap-0 overflow-hidden p-0"
        preventOverlayClickCapturePropagation
      >
        <DialogHeader className="space-y-1 border-b border-slate-200 px-6 py-4">
          <DialogTitle className="text-left text-lg font-semibold text-slate-900">
            Pulse
          </DialogTitle>
          <DialogDescription className="text-left text-sm text-slate-500">
            {companyName} · {jobTitle}
          </DialogDescription>
        </DialogHeader>

        <Column className="min-h-0 flex-1 gap-5 overflow-y-auto px-6 py-4">
          <Column className="gap-2">
            <label
              htmlFor="health-matrix-pulse-draft"
              className="text-sm font-medium text-slate-700"
            >
              New pulse
            </label>
            <Textarea
              id="health-matrix-pulse-draft"
              value={draft}
              onChange={(event) => {
                setDraft(event.target.value);
              }}
              onKeyDown={handleDraftKeyDown}
              className="min-h-[120px] resize-y text-sm"
              placeholder="Add a pulse note..."
            />
          </Column>

          <Column className="gap-2">
            <h3 className="text-sm font-medium text-slate-700">History</h3>
            <PulseHistoryTable
              history={sortedHistory}
              isError={isHistoryError}
              isLoading={isHistoryLoading}
            />
          </Column>
        </Column>

        <DialogFooter className="border-t border-slate-200 px-6 py-4">
          <Button
            type="button"
            variant="outlineSecondary"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} disabled={!canSave}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// authorName attributes the note when the cell shows another staffer's pulse
// (the dashboard All view); omitted when the pulse is the viewer's own.
export const PulseCell = ({
  authorName,
  job,
}: {
  authorName?: string | null;
  job: PulseCellJob;
}) => {
  const latestPulseDescription = getPulseDescription(job);
  const [pulse, setPulse] = useState(latestPulseDescription);
  const [pulseCreatedAt, setPulseCreatedAt] = useState(
    job.pulseCreatedAt ?? null,
  );
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { mutate: createJobUpdate, isPending } = useCreateJobUpdate();
  const {
    data: jobUpdates = [],
    isError: isHistoryError,
    isLoading: isHistoryLoading,
  } = useGetJobUpdates({
    companyId: job.companyId,
    enabled: isDialogOpen,
    jobId: job.id,
    uniqueDescriptions: true,
  });
  const history = useMemo(
    () => getDescriptionHistory(jobUpdates),
    [jobUpdates],
  );

  useEffect(() => {
    setPulse(latestPulseDescription);
    setPulseCreatedAt(job.pulseCreatedAt ?? null);
  }, [latestPulseDescription, job.pulseCreatedAt]);

  const openDialog = () => {
    setIsDialogOpen(true);
  };

  const handleSave = (nextPulse: string) => {
    if (nextPulse.length === 0 || nextPulse === pulse) {
      return;
    }

    setPulse(nextPulse);
    createJobUpdate(
      {
        companyId: job.companyId,
        jobId: job.id,
        createJobUpdateRequest: {
          description: nextPulse,
        },
      },
      {
        onSuccess: (newJobUpdate) => {
          setPulse(newJobUpdate.description ?? nextPulse);
          setPulseCreatedAt(newJobUpdate.createdAt);
        },
      },
    );
  };

  return (
    <>
      <Row className="w-full min-w-0 items-start gap-2 pr-1">
        <Column className="min-w-0 flex-1 gap-0.5 overflow-hidden">
          {pulse ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <p className={cn("cursor-default", PULSE_TEXT_CLASS)}>
                  {authorName ? (
                    <span className="font-semibold">{authorName}: </span>
                  ) : null}
                  {pulse}
                </p>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs">
                <p className="text-sm leading-snug">
                  {authorName ? (
                    <span className="font-semibold">{authorName}: </span>
                  ) : null}
                  {pulse}
                </p>
              </TooltipContent>
            </Tooltip>
          ) : null}
          {pulse && pulseCreatedAt ? (
            <TimeAgoInTableCell
              className="w-fit cursor-default text-[11px]"
              timestamp={pulseCreatedAt}
            />
          ) : null}
        </Column>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
              onClick={openDialog}
              aria-label="Add pulse"
            >
              <SquarePenIcon className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">Add pulse</TooltipContent>
        </Tooltip>
      </Row>

      <PulseEditDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        companyName={job.companyName}
        jobTitle={job.title}
        pulse={pulse}
        history={history}
        isHistoryError={isHistoryError}
        isHistoryLoading={isHistoryLoading}
        onSave={handleSave}
        isSaving={isPending}
      />
    </>
  );
};
